import { useTranslation } from 'react-i18next';

import type { TFeaturedIconColor } from 'libs/design-system/src/types/featured_icon/featuredIcon';
import {
  ArrowsRepeat,
  CircleCheck,
  Receipt,
  Wallet,
} from '@unified-checkout/design-system/icons/index';
import {
  FeaturedIcon,
  FormatCurrencyForDisplay,
  SkeletonBase,
  SkeletonLoader,
} from '@unified-checkout/design-system';

import type {
  IBreakdownProps,
  ICompactViewCard,
} from '@app/dashboard/types/innerLayout/balance_and_transfers/single-transfer/breakdown';
import { useLangStore } from '@app/dashboard/store/lang';
import { COMPACT_VIEW_OPTIONS } from '@app/dashboard/constants/balance_and_transfers/single-transfer';
import { formatAmountWithCurrency } from '@app/dashboard/views/pages/inner_layout/BalanceAndTransfers/utils';

import styles from './compact-view.module.scss';

const CompactView = ({ breakdown, isLoading }: IBreakdownProps) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const {
    currency = '',
    amount_deposit,
    balance_transfer,
    chargeback_amount,
    delivery_fee,
    gross_amount,
    net_amount,
    processing_fee,
    refund_amount,
    subscription_amount,
    total_deductions,
    total_fees,
    transfer_fee,
    total_adjustments,
    success_message = '',
    vat,
  } = breakdown || {};

  const formattedAmount = (amount: number = 0) => {
    return formatAmountWithCurrency({
      amount,
      currency,
      lang,
    });
  };

  const SUMMARY_CARD_ITEMS: ICompactViewCard[] = [
    {
      id: COMPACT_VIEW_OPTIONS.NET_AMOUNT,
      icon: Wallet,
      title: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.netAmount'
      ),
      subtitle: formattedAmount(net_amount),
      body: [
        {
          label: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.grossAmountOnly'
          ),
          value: {
            text: formattedAmount(gross_amount),
            isBold: true,
          },
        },
        {
          label: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.totalFees'
          ),
          value: `- ${formattedAmount(total_fees)}`,
        },
        {
          label: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.totalAdjustments'
          ),
          value: `- ${formattedAmount(total_adjustments)}`,
        },
      ],
      footer: {
        label: t(
          'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.totalDeductionOnly'
        ),
        value: `- ${formattedAmount(total_deductions)}`,
      },
      banner: {
        icon: CircleCheck,
        text: success_message,
        color: 'green' as TFeaturedIconColor,
      },
      haveLabelDots: false,
    },
    {
      id: COMPACT_VIEW_OPTIONS.FEES,
      icon: Receipt,
      title: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.fieldsAndDeductions'
      ),
      body: [
        {
          label: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.processingFees'
          ),
          value: `- ${formattedAmount(processing_fee)}`,
        },
        {
          label: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.vat'
          ),
          value: `- ${formattedAmount(vat)}`,
        },
        {
          label: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.transferFees'
          ),
          value: `- ${formattedAmount(transfer_fee)}`,
        },
        {
          label: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.deliveryFee'
          ),
          value: `- ${formattedAmount(delivery_fee)}`,
        },
      ],
      footer: {
        label: {
          text: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.totalFees'
          ),
          isBold: true,
        },
        value: `- ${formattedAmount(total_fees)}`,
      },
      haveLabelDots: true,
    },
    {
      id: COMPACT_VIEW_OPTIONS.ADJUSTMENTS,
      icon: ArrowsRepeat,
      title: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.adjustments'
      ),
      body: [
        {
          label: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.refundAmount'
          ),
          value: `- ${formattedAmount(refund_amount)}`,
        },
        {
          label: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.chargebackAmount'
          ),
          value: `- ${formattedAmount(chargeback_amount)}`,
        },
        {
          label: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.subscriptionAmount'
          ),
          value: `- ${formattedAmount(subscription_amount)}`,
        },
        {
          label: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.amountDeposit'
          ),
          value: {
            text: `+ ${formattedAmount(amount_deposit)}`,
            color: 'green',
          },
        },
        {
          label: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.balanceTransfer'
          ),
          value: {
            text: `± ${formattedAmount(balance_transfer)}`,
            color: 'blue',
          },
        },
      ],
      footer: {
        label: {
          text: t(
            'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.totalAdjustments'
          ),
          isBold: true,
        },
        value: `- ${formattedAmount(total_adjustments)}`,
      },

      haveLabelDots: true,
    },
  ];

  return (
    <div className={styles['breakdown']}>
      <div className={styles['breakdown__cards']}>
        {SUMMARY_CARD_ITEMS.map((item) => {
          return (
            <div
              key={item.id}
              className={styles['breakdown__cards-card']}
              data-break-down-card={item.id}
              data-break-down-card-loading={isLoading}
            >
              <div className={styles['breakdown__cards-card-icon']}>
                {isLoading ? <SkeletonBase /> : item.icon}
              </div>
              <p className={styles['breakdown__cards-card-title']}>
                {isLoading ? (
                  <SkeletonBase height="1.75rem" width="10rem" />
                ) : (
                  item.title
                )}
              </p>
              {item.subtitle && !isLoading && (
                <p className={styles['breakdown__cards-card-subtitle']}>
                  {FormatCurrencyForDisplay(item.subtitle)}
                </p>
              )}
              {isLoading ? (
                <SkeletonLoader
                  noOfRows={4}
                  type="transparent_box"
                  width="100%"
                  height="16.875rem"
                />
              ) : (
                item.body.map((row, idx) => (
                  <div
                    className={styles['breakdown__cards-card-body']}
                    key={idx}
                  >
                    <div
                      className={styles['breakdown__cards-card-body-row']}
                      data-card-value-bold={
                        typeof row.value === 'object' && row.value.isBold
                      }
                      data-card-value-color={
                        typeof row.value === 'object' && row.value?.color
                      }
                      data-card-body-row-has-dots={item.haveLabelDots}
                    >
                      <p
                        className={
                          styles['breakdown__cards-card-body-row-label']
                        }
                      >
                        {row.label}
                      </p>
                      <p
                        className={
                          styles['breakdown__cards-card-body-row-value']
                        }
                      >
                        {typeof row.value === 'string'
                          ? FormatCurrencyForDisplay(row.value)
                          : FormatCurrencyForDisplay(row.value.text)}
                      </p>
                    </div>
                  </div>
                ))
              )}

              <div className={styles['breakdown__cards-card-footer']}>
                {isLoading ? (
                  <SkeletonBase height="2rem" />
                ) : (
                  <>
                    <p
                      className={styles['breakdown__cards-card-footer-label']}
                      data-card-footer-label-bold={
                        typeof item.footer.label === 'object' &&
                        item.footer.label.isBold
                      }
                    >
                      {typeof item.footer.label === 'string'
                        ? item.footer.label
                        : item.footer.label.text}
                    </p>
                    <p className={styles['breakdown__cards-card-footer-value']}>
                      {FormatCurrencyForDisplay(item.footer.value)}
                    </p>
                  </>
                )}
              </div>
              {item.banner && !isLoading && (
                <div className={styles['breakdown__cards-card-banner']}>
                  <FeaturedIcon
                    color={item.banner.color}
                    size="lg"
                    icon={item.banner.icon}
                  />
                  <p>{item.banner.text}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CompactView;
